import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Button from "@/components/UI/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import images from "@/utils/images";
import { isAdminCSM } from "@/utils/authToken";
import {
  List,
  ListBody,
  ListHeader,
  ListItem,
  ListTitleBar,
  TitleBarItem,
} from "@/components/UI/ListView";
import { parseErrorMessage } from "@/lib/helpers/parseErrorMessage";
import type { PatientFile } from "@/features/patient/types/PatientFile";
import { showToast } from "@/providers/ToastProvider";
import { uploadAssociatedFiles } from "@/utils/fileUtil";
import UploadFile from "@/components/Provider/PatientView/UploadFile";

import { fetchEntityAndFiles } from "./helpers/fetchEntityAndFiles";
import { FILE_LIST_CONFIG } from "./constants/config";
import type { FileListEntity, FileListProps } from "./types";
import { FileRow } from "./components/FileRow";

export function FileList({ type }: FileListProps) {
  const { patientId } = useParams<{ patientId: string }>();
  const { referralCode } = useParams<{ referralCode: string }>();
  const { screening_id: screeningId } = useParams<{ screening_id: string }>();
  const history = useHistory();

  const [entity, setEntity] = useState<FileListEntity>(null);
  const [files, setFiles] = useState<PatientFile[]>([]);
  const [id, setId] = useState<string | null>(null);
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<File[]>([]);
  const [showUploadFileCard, setShowUploadFileCard] = useState(false);

  const config = FILE_LIST_CONFIG[type];

  useEffect(() => {
    let isMounted = true;

    fetchEntityAndFiles(type, { patientId, referralCode, screeningId })
      .then((data) => {
        if (!isMounted) return;

        setEntity(data.entity);
        setFiles(data.files);
        setId(data.id);
      })
      .catch((error) => {
        captureErrorMessage(parseError({ error, fallback: "Failed to load associated files" }), {
          tags: { component: "FileList", errorType: "fetch_associated_files", type },
        });
        showToast([{ heading: "Failed to load files.", variant: "error" }]);
      });

    return () => {
      isMounted = false;
    };
  }, [type, patientId, referralCode, screeningId]);

  const handleViewFile = (key: string) => {
    if (!id) return;

    void config.viewFile(id, key);
  };

  const handleFileInputChange = (target: DataTransfer | HTMLInputElement | null) => {
    const incomingFiles = target?.files;

    if (!incomingFiles || incomingFiles.length === 0) return;

    setFilesToBeUploaded((prev) => [...prev, ...Array.from(incomingFiles)]);
  };

  const handleFileDelete = (index: number) => {
    setFilesToBeUploaded((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleInsertFiles = async () => {
    if (!id) return;

    try {
      const newFiles = (await uploadAssociatedFiles(
        filesToBeUploaded,
        type,
        id,
        config.fileType,
      )) as PatientFile[];

      setFiles((prev) => [...newFiles, ...prev]);
      setFilesToBeUploaded([]);
      setShowUploadFileCard(false);
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Failed to upload associated files" }), {
        tags: { component: "FileList", errorType: "upload_files", id, type },
      });
      showToast([{ heading: parseErrorMessage(error), variant: "error" }]);
    }
  };

  const renderControls = () => (
    <div className="d-flex">
      {!isAdminCSM() && (
        <Button
          className="Btn d-none d-lg-block Btn--pri Btn-sm ml-4 fs-16 text-center text-white"
          onClick={() => setShowUploadFileCard(true)}
        >
          Upload
        </Button>
      )}
    </div>
  );

  const name = entity ? `${entity.first_name} ${entity.last_name}` : "";

  return (
    <div>
      <div>
        <div className="backLink-wpr mx-auto mt-4 d-none d-lg-block pointer">
          {entity && (
            <button
              className="PatientProfile-back-link border-0 bg-transparent p-0"
              onClick={() => history.goBack()}
              type="button"
            >
              <img alt="" className="img-fluid" src={images("./common/solidBckBtn.svg")} />
              <p className="d-inline ml-2">{config.backLinkText}</p>
            </button>
          )}
        </div>
        <ListBody>
          {showUploadFileCard && (
            <UploadFile
              files={filesToBeUploaded}
              hideModal={() => setShowUploadFileCard(false)}
              onFileRemove={handleFileDelete}
              onInputChange={handleFileInputChange}
              onInsertFiles={handleInsertFiles}
            />
          )}
          {entity && <ListHeader render={renderControls} title={name + "'s Files"} />}
          <ListTitleBar>
            <TitleBarItem classes="col-4" text="Load Date" />
            <TitleBarItem classes="col-6" text="File Name" />
            <TitleBarItem classes="col-2" text="" />
          </ListTitleBar>
          <List>
            {files.filter(Boolean).map((file) => (
              <ListItem
                clickHandler={() => handleViewFile(file.file_key)}
                clickOnMobile
                key={file.file_key}
              >
                <FileRow file={file} onView={handleViewFile} />
              </ListItem>
            ))}
          </List>
        </ListBody>
      </div>
    </div>
  );
}
